import React, { useState } from 'react';
import { Close, Search, AutoAwesome } from '@mui/icons-material';

const DrillDownModal = ({
  isOpen,
  onClose,
  title,
  metricType,
  data,
  loading,
  formatCurrency,
  formatDate,
  formatTime,
  navigate
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  if (!isOpen) return null;

  // Filter data based on search
  const filteredData = data.filter(item => {
    const searchLower = searchTerm.toLowerCase();
    return (
      item.loanNumber?.toString().includes(searchLower) ||
      item.clientName?.toLowerCase().includes(searchLower) ||
      item.notes?.toLowerCase().includes(searchLower) ||
      item.agentName?.toLowerCase().includes(searchLower)
    );
  });

  // Get resolution type display info
  const getResolutionTypeInfo = (type) => {
    const types = {
      'client_picked_up': { label: 'Client Picked Up', color: 'bg-green-100 text-green-800' },
      'client_no_answer': { label: "Client Didn't Pick Up", color: 'bg-yellow-100 text-yellow-800' },
      'promise_to_pay': { label: 'Promise to Pay', color: 'bg-blue-100 text-blue-800' },
      'payment_collected': { label: 'Payment Collected', color: 'bg-green-100 text-green-800' },
      'resolved': { label: 'Resolved', color: 'bg-purple-100 text-purple-800' },
      'wrong_number': { label: 'Wrong Number', color: 'bg-red-100 text-red-800' },
      'dispute': { label: 'Dispute', color: 'bg-orange-100 text-orange-800' },
      'other': { label: 'Other', color: 'bg-gray-100 text-gray-800' }
    };
    return types[type] || { label: type, color: 'bg-gray-100 text-gray-800' };
  };

  // Get promise status badge
  const getPromiseStatusBadge = (status) => {
    const badges = {
      'fulfilled': { label: 'Fulfilled', color: 'bg-green-100 text-green-800' },
      'partially_fulfilled': { label: 'Partial', color: 'bg-yellow-100 text-yellow-800' },
      'pending': { label: 'Pending', color: 'bg-blue-100 text-blue-800' },
      'broken': { label: 'Broken', color: 'bg-red-100 text-red-800' }
    };
    return badges[status] || { label: status, color: 'bg-gray-100 text-gray-800' };
  };

  return (
    <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex justify-center items-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">

        {/* Header */}
        <div className="border-b p-6 flex justify-between items-center bg-gradient-to-r from-cyan-50 to-blue-50">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
            <p className="text-sm text-gray-600 mt-1">
              {loading ? 'Loading...' : `${filteredData.length} of ${data.length} items`}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <Close />
          </button>
        </div>

        {/* Search Bar */}
        <div className="p-4 border-b bg-gray-50">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by loan number, client name, notes, or agent..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
            </div>
          ) : filteredData.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <p className="text-lg">No items found</p>
              {searchTerm && (
                <p className="text-sm mt-2">Try adjusting your search criteria</p>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {filteredData.map((item) => {
                const resolutionInfo = getResolutionTypeInfo(item.resolutionType);

                return (
                  <div
                    key={item._id}
                    className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 hover:border-cyan-300 transition-all"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">

                        {/* Loan Number & Client Name */}
                        <div className="flex items-center gap-3 mb-2">
                          <button
                            onClick={() => {
                              navigate(`/loan/${item.loanId}`);
                              onClose();
                            }}
                            className="text-cyan-600 hover:text-cyan-700 font-bold text-lg hover:underline"
                          >
                            Loan #{item.loanNumber}
                          </button>
                          <span className="text-gray-400">•</span>
                          <span className="text-gray-900 font-medium">{item.clientName}</span>
                        </div>

                        {/* Resolution Type Badge (Consolidated for Promises) */}
                        <div className="flex items-center gap-2 mb-2">
                          {/* Consolidated Promise Badge or Regular Resolution Badge */}
                          {item.resolutionType === 'promise_to_pay' ? (
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              item.promiseStatus === 'fulfilled' ? 'bg-green-100 text-green-800' :
                              item.promiseStatus === 'broken' ? 'bg-red-100 text-red-800' :
                              item.promiseStatus === 'partially_fulfilled' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-yellow-100 text-yellow-800'
                            }`}>
                              {item.promiseStatus === 'fulfilled' ? 'Promise Fulfilled' :
                               item.promiseStatus === 'broken' ? 'Promise Broken' :
                               item.promiseStatus === 'partially_fulfilled' ? 'Promise Partial' :
                               'Promise Pending'}
                            </span>
                          ) : (
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${resolutionInfo.color}`}>
                              {resolutionInfo.label}
                            </span>
                          )}

                          {/* Promise-to-Pay Fulfilled badge for payments_collected */}
                          {metricType === 'payments-collected' && item.isAutoCreated && (
                            <span className="px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 flex items-center gap-1">
                              <AutoAwesome style={{ fontSize: 14 }} />
                              Promise-to-Pay Fulfilled
                            </span>
                          )}
                        </div>

                        {/* Promise Details (for promise_to_pay metric) */}
                        {metricType === 'promises-to-pay' && (
                          <div className="mb-2 space-y-1">
                            <div className="flex items-center gap-4 text-sm">
                              <span className="text-blue-600 font-semibold">
                                Amount: {formatCurrency(item.promiseAmount)}
                              </span>
                              <span className="text-gray-600">
                                Due: {formatDate(item.promiseDate)}
                              </span>
                            </div>
                            {item.promiseStatus === 'partially_fulfilled' && item.fulfillmentPercentage > 0 && (
                              <div className="text-xs text-gray-600">
                                {item.fulfillmentPercentage}% paid ({formatCurrency(item.amountPaid)})
                              </div>
                            )}
                          </div>
                        )}

                        {/* Payment Amount (for payments_collected metric) */}
                        {metricType === 'payments-collected' && item.promiseAmount && (
                          <div className="mb-2">
                            <span className="text-green-600 font-semibold text-sm">
                              Amount: {formatCurrency(item.promiseAmount)}
                            </span>
                          </div>
                        )}

                        {/* Notes */}
                        {item.notes && (
                          <p className="text-sm text-gray-600 mt-2 bg-gray-50 p-2 rounded border border-gray-200">
                            {item.notes}
                          </p>
                        )}
                      </div>

                      {/* Agent & Timestamp */}
                      <div className="text-right text-sm text-gray-500 ml-4 min-w-[150px]">
                        <div className="font-medium text-gray-700">{item.agentName}</div>
                        <div>{formatDate(item.createdAt)}</div>
                        <div>{formatTime(item.createdAt)}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t p-4 bg-gray-50 flex justify-between items-center">
          <p className="text-sm text-gray-600">
            Click on any loan number to view full details
          </p>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default DrillDownModal;
